import React from 'react';
import {
    Box,
    Button,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { TableVirtuoso } from 'react-virtuoso';
import { IST_HM } from '../chartTheme';
import { useTickTicks } from '../api';

const PAGE_SIZE = 500;
const COLUMNS = ['Time', 'LTP', 'LTQ', 'Bid', 'Bid Qty', 'Ask', 'Ask Qty', 'OI'];

function formatTs(iso) {
    try {
        return IST_HM.format(new Date(iso));
    } catch {
        return iso;
    }
}

function fmtNum(v, digits = 2) {
    if (v == null || Number.isNaN(v)) return '—';
    return typeof v === 'number' ? v.toFixed(digits) : v;
}

export default function TicksTable({ symbol, date }) {
    const [offset, setOffset] = React.useState(0);
    const { data, isLoading, isError, error } = useTickTicks(symbol, date, offset, PAGE_SIZE);

    React.useEffect(() => {
        setOffset(0);
    }, [symbol, date]);

    const ticks = data?.ticks || [];
    const hasMore = ticks.length === PAGE_SIZE;

    if (!symbol || !date) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Select a symbol to load raw ticks.
            </Typography>
        );
    }

    if (isLoading && !ticks.length) {
        return <LinearProgress sx={{ my: 2 }} />;
    }

    if (isError) {
        return (
            <Typography variant="body2" color="error.main" sx={{ py: 2 }}>
                {error.message}
            </Typography>
        );
    }

    if (!ticks.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No ticks for this selection.
            </Typography>
        );
    }

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Showing rows {offset + 1}–{offset + ticks.length}
            </Typography>
            <TableContainer component={Box} sx={{ height: 360 }}>
                <TableVirtuoso
                    data={ticks}
                    components={{
                        Table: (props) => <Table {...props} size="small" stickyHeader />,
                        TableHead,
                        TableRow,
                        TableBody,
                    }}
                    fixedHeaderContent={() => (
                        <TableRow>
                            {COLUMNS.map((col) => (
                                <TableCell key={col} sx={{ fontWeight: 600, fontSize: 11 }}>
                                    {col}
                                </TableCell>
                            ))}
                        </TableRow>
                    )}
                    itemContent={(_index, row) => (
                        <>
                            <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>
                                {formatTs(row.ts)}
                            </TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{fmtNum(row.ltp)}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{row.ltq ?? '—'}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{fmtNum(row.bid)}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{row.bid_qty ?? '—'}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{fmtNum(row.ask)}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{row.ask_qty ?? '—'}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{row.oi ?? '—'}</TableCell>
                        </>
                    )}
                />
            </TableContainer>
            <Box display="flex" gap={1} mt={1}>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                    Previous
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!hasMore}
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                    Next
                </Button>
            </Box>
        </Box>
    );
}
